import {
  AppSearchIconKey,
  AppSearchItemType,
  DEFAULT_ZOOM,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useCorridorMapListData,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
} from '@kinetic-ui/shared';
import bbox from '@turf/bbox';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/const';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { TunnelLayerState } from '../layers/TunnelLayer/types';
import tunnelIcon from './tunnelIcon.svg';
import { useTranslation } from 'react-i18next';
import { CorridorType, FilterOperator } from '@kinetic-ui/data-layer';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();
  const { t } = useTranslation('common');

  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const filters = [
    {
      operator: FilterOperator.Equals,
      value: CorridorType.Tunnel,
      field: 'type',
    },
  ];

  const { rowData } = useCorridorMapListData({
    activeFilters: filters,
    searchText,
    skip: !searchText,
  });
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const tunnel = rowData.find((e) => e.id === id);
        if (tunnel?.name) {
          addSearchHistory({
            name: tunnel.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Tunnel,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      !selectedLayers.includes(TunnelLayerState.TUNNELS) &&
        setSelectedLayers([...selectedLayers, TunnelLayerState.TUNNELS]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      rowData,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {rowData.map((tunnel) => (
        <MapSearchResultItem
          key={`searchItem_${tunnel.id}`}
          name={tunnel.name ?? ''}
          onClick={(openInNewWindow) => onClick(tunnel.id, tunnel.geometry, openInNewWindow)}
          icon={<img src={tunnelIcon} alt={t('TUNNELS')} />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
